import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Users, BookOpen, Award, Globe2, Building2, Briefcase, Video } from "lucide-react";

const COUNTRIES = [
  { name: "India", langs: "English, Hindi", currency: "INR", tz: "IST", centers: "100+", status: "Active" },
  { name: "Philippines", langs: "English, Tagalog", currency: "PHP", tz: "PHT", centers: "Planned", status: "Planned" },
  { name: "United Kingdom", langs: "English", currency: "GBP", tz: "GMT", centers: "Planned", status: "Planned" },
  { name: "France", langs: "French", currency: "EUR", tz: "CET", centers: "Planned", status: "Planned" },
  { name: "Switzerland", langs: "French, German", currency: "CHF", tz: "CET", centers: "Planned", status: "Planned" },
];

const CENTERS = [
  { name: "FSCD Delhi Community Center", city: "New Delhi", programs: 3, students: 8 },
  { name: "FSCD Jaipur Eye Camp Hub", city: "Jaipur", programs: 2, students: 5 },
  { name: "FSCD Lucknow Skills Center", city: "Lucknow", programs: 3, students: 4 },
  { name: "FSCD Pune Training Site", city: "Pune", programs: 2, students: 3 },
];

function Stat({ icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3">
      <span className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0">{icon}</span>
      <div><p className="text-2xl font-heading font-extrabold text-slate-900">{value}</p><p className="text-xs text-slate-500">{label}</p></div>
    </div>
  );
}

export default function SuperAdmin() {
  const [stats, setStats] = useState(null);
  const { pick } = useLang();
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <p className="text-slate-500">Loading…</p>;
  const placements = stats.funnel?.hired || 0;
  const base = Math.max(1, stats.trainees);
  const trend = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"].map((m, i) => ({
    month: m, students: Math.max(0, Math.round(base * (0.2 + i * 0.16))), certified: Math.max(0, Math.round(stats.certificates * (0.08 + i * 0.184))),
  }));
  const trackBars = stats.per_track.map((r) => ({ name: pick(r.title).split(" ").slice(0, 2).join(" "), enrolled: r.enrolled, certified: r.certified }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Global Dashboard</h1>
        <Link to="/admin" data-testid="open-program-admin-link" className="h-10 px-4 rounded-xl bg-slate-900 text-white text-sm font-semibold flex items-center">Program Admin View</Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={<Users size={20} />} label="Total Students" value={stats.trainees} testid="sa-students" />
        <Stat icon={<BookOpen size={20} />} label="Active Courses" value={12} testid="sa-courses" />
        <Stat icon={<Globe2 size={20} />} label="Active Countries" value="1 / 5" testid="sa-countries" />
        <Stat icon={<Building2 size={20} />} label="Training Centers" value={CENTERS.length} testid="sa-centers" />
        <Stat icon={<Award size={20} />} label="Certifications Issued" value={stats.certificates} testid="sa-certs" />
        <Stat icon={<Briefcase size={20} />} label="Employment Placements" value={placements} testid="sa-placements" />
        <Stat icon={<Video size={20} />} label="Pending Practicals" value={stats.pending_videos} testid="sa-pending" />
        <Stat icon={<Users size={20} />} label="Active Partners" value={3} testid="sa-partners" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <section className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-bold text-slate-900">Global Growth</h2>
            <span className="text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-400 px-2.5 py-1 rounded-full">2026 · illustrative</span>
          </div>
          <ResponsiveContainer width="100%" height={230}>
            <AreaChart data={trend} margin={{ left: -20, right: 8, top: 4 }}>
              <defs>
                <linearGradient id="gSt" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0F766E" stopOpacity={0.35} /><stop offset="100%" stopColor="#0F766E" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Area type="monotone" dataKey="students" stroke="#0F766E" strokeWidth={2.5} fill="url(#gSt)" name="Students" />
              <Area type="monotone" dataKey="certified" stroke="#F59E0B" strokeWidth={2.5} fill="none" name="Certified" />
            </AreaChart>
          </ResponsiveContainer>
        </section>
        <section className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm">
          <h2 className="font-heading font-bold text-slate-900 mb-3">Enrolled vs Certified by Track</h2>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={trackBars} margin={{ left: -20, right: 8, top: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Bar dataKey="enrolled" fill="#0F766E" radius={[8, 8, 0, 0]} maxBarSize={36} name="Enrolled" />
              <Bar dataKey="certified" fill="#F59E0B" radius={[8, 8, 0, 0]} maxBarSize={36} name="Certified" />
            </BarChart>
          </ResponsiveContainer>
        </section>
      </div>

      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
        <h2 className="font-heading font-bold text-lg text-slate-900">Students by Vertical</h2>
        {[["Healthcare & Eye Care", stats.enrollments, "bg-sky-600"], ["Financial Literacy & FinTech", 0, "bg-teal-500"], ["Skilled Trades", 0, "bg-amber-500"], ["Sustainable Communities", 0, "bg-emerald-600"]].map(([v, n, c]) => (
          <div key={v} className="flex items-center gap-3 text-sm">
            <span className="w-56 text-slate-700 shrink-0 truncate">{v}</span>
            <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full ${c}`} style={{ width: `${Math.min(100, n * 12)}%` }} /></div>
            <span className="w-8 text-right font-semibold text-slate-800">{n}</span>
          </div>
        ))}
        <p className="text-xs text-slate-400">Financial Literacy, Skilled Trades and Sustainable Communities courses launch in Phase 2.</p>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
        <h2 className="font-heading font-bold text-lg text-slate-900">Countries</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="countries-table">
            <thead><tr className="text-left text-xs text-slate-400 uppercase tracking-wider"><th className="py-2 pr-4">Country</th><th className="pr-4">Languages</th><th className="pr-4">Currency</th><th className="pr-4">Timezone</th><th className="pr-4">Centers</th><th>Status</th></tr></thead>
            <tbody>
              {COUNTRIES.map((c) => (
                <tr key={c.name} className="border-t border-slate-100">
                  <td className="py-2.5 pr-4 font-medium text-slate-800">{c.name}</td>
                  <td className="pr-4">{c.langs}</td><td className="pr-4">{c.currency}</td><td className="pr-4">{c.tz}</td><td className="pr-4">{c.centers}</td>
                  <td><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${c.status === "Active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>{c.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="font-heading font-bold text-lg text-slate-900">Training Centers — India</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {CENTERS.map((c) => (
            <div key={c.name} className="bg-white rounded-2xl border border-slate-200 p-4 space-y-1">
              <p className="font-semibold text-slate-900 text-sm leading-snug">{c.name}</p>
              <p className="text-xs text-slate-500">{c.city}</p>
              <p className="text-xs text-slate-500">{c.programs} programs · {c.students} students</p>
              <span className="inline-block text-[10px] font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">Active</span>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
        <h2 className="font-heading font-bold text-lg text-slate-900">Track Performance</h2>
        {stats.per_track.map((r) => (
          <div key={r.track_id} className="flex items-center gap-3 text-sm">
            <span className="w-56 text-slate-700 shrink-0 truncate">{pick(r.title)}</span>
            <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-teal-600" style={{ width: `${r.avg_progress_pct}%` }} /></div>
            <span className="w-28 text-right text-slate-500">{r.enrolled} enrolled · {r.certified} certified</span>
          </div>
        ))}
      </section>
    </div>
  );
}
